import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import styles from "./Checkbox.module.less";

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value"> {
  label?: React.ReactNode | string;
  className?: string;
  indeterminate?: boolean;
  error?: boolean;
  boxed?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children?: React.ReactNode;
  value?: React.InputHTMLAttributes<HTMLInputElement>["value"] | boolean;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { label, className, indeterminate = false, error = false, boxed, children, checked, disabled, onChange, value, ...rest },
    forwardedRef
  ) => {
    const ref = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (ref.current) {
        ref.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const labelOnClick = (e: React.MouseEvent<HTMLLabelElement>): void => {
      e.stopPropagation();
    };

    React.useImperativeHandle(forwardedRef, () => ref.current!, [ref]);

    const checkboxClass = cls(
      {
        [styles.checkboxChecked]: checked,
      },
      className
    );
    const inputClass = cls({
      [styles.checkboxInput]: true,
      [styles.checkboxInputError]: error,
    });
    const labelClass = cls({
      [styles.checkboxInputLabel]: true,
      [styles.checkboxInputLabelEmpty]: !label,
    });
    const boxClass = cls({
      [styles.checkboxBoxed]: boxed || !!children,
      [styles.checkboxWithChildren]: !!children,
    });
    return (
      <div className={checkboxClass}>
        <div className={boxClass}>
          <label // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/label-has-associated-control
            className={styles.checkboxWrapper}
            onClick={labelOnClick}
          >
            <input
              {...rest}
              ref={ref}
              disabled={disabled}
              checked={checked}
              onChange={onChange}
              type="checkbox"
              aria-disabled={disabled}
              aria-checked={indeterminate ? "mixed" : checked}
              className={inputClass}
              value={`${value}`}
            />
            <span className={labelClass}>{label}</span>
          </label>
        </div>
        {!!children && checked ? <div className={styles.checkboxExpanded}>{children}</div> : null}
      </div>
    );
  }
);

export default Checkbox;
